import React from 'react';
import { Appointment } from '@weave/schema-gen-ts/dist/schemas/schedule/calendar-events/v1/calendar_events.pb';
import { useAppScopeStore } from '@frontend/scope';
import { isEmpty } from '@frontend/validation';
import { theme } from '@frontend/theme';
import { Modal, useModalControl, SpinningLoader, styles, ModalControlModalProps } from '@frontend/design-system';
import { AppointmentEventCard } from '../components/schedule-calendar-components/appointment-event-card';
import { PersonStatusIndicator } from '../components/schedule-calendar-components/appointment-event-card/person-status-indicator';
import { PRIMARY_EVENT_COLOR } from '../constants';
import { ExtendedAppointment } from './use-appointments-info-store';
import { useGetAppointmentInfo } from './use-get-appointment-info';
import { useGetCalendarEventInfo } from './use-get-calendar-event-info';

type UseGetAppointmentDetailsProps = {
  appointmentId: string;
  locationId: string;
  enabled: boolean;
  appointments?: Appointment[];
  eventColor?: string;
  onModalClose?: () => void;
};

export const useGetAppointmentDetails = ({
  appointmentId,
  locationId,
  onModalClose,
  eventColor,
  appointments,
  enabled,
}: UseGetAppointmentDetailsProps) => {
  const modal = useModalControl();

  const handleOnCloseModal = () => {
    onModalClose?.();
    modal?.closeModal();
  };

  return {
    Modal: (
      <AppointmentDetailsModal
        onCloseModal={handleOnCloseModal}
        modalProps={modal.modalProps}
        appointmentId={appointmentId}
        locationId={locationId}
        eventColor={eventColor}
        appointments={appointments}
        isEnabled={enabled}
      />
    ),
    triggerProps: { ...modal.triggerProps },
    closeModal: handleOnCloseModal,
  };
};

type AppointmentDetailsModalProps = {
  modalProps: ModalControlModalProps;
  appointmentId: string;
  locationId: string;
  isEnabled: boolean;
  onCloseModal: () => void;
  appointments?: Appointment[];
  eventColor?: string;
};

const AppointmentDetailsModal = React.memo(
  ({
    modalProps,
    locationId,
    appointmentId,
    eventColor,
    appointments,
    onCloseModal,
    isEnabled,
  }: AppointmentDetailsModalProps) => {
    const { getScopeName } = useAppScopeStore();

    const appointmentInfo = appointments?.find((appointment: ExtendedAppointment) => appointment.id === appointmentId);

    const { appointmentDetails: calendarViewAppointment } = useGetAppointmentInfo(appointmentId);

    const existsInFetchedAppointments = appointmentInfo ?? calendarViewAppointment;

    const { onClose, ...restModalProps } = modalProps;

    const onModalClose = () => {
      onClose();
      onCloseModal();
    };

    const { eventInfo, isLoading } = useGetCalendarEventInfo({
      eventId: appointmentId,
      locationId,
      enabled: isEnabled && !existsInFetchedAppointments && modalProps.show,
    });

    const appointmentDetails = !!existsInFetchedAppointments ? existsInFetchedAppointments : eventInfo;

    // Set the practitioner name to an empty string, if the provider is unassigned
    if (appointmentDetails && !appointmentDetails?.providerIds) {
      appointmentDetails.practitionerName = '';
    }

    const getPrimaryColor = (appointment: ExtendedAppointment) => {
      if (!appointment || isEmpty(appointment)) {
        return undefined;
      }
      if (appointment && 'displayOptions' in appointment && appointment.displayOptions?.primaryColor) {
        return appointment.displayOptions.primaryColor;
      }
      return undefined;
    };

    const primaryColor = eventColor || getPrimaryColor(appointmentDetails ?? {}) || PRIMARY_EVENT_COLOR;

    const personStatus = appointmentDetails?.person?.status ?? '';

    const referenceId =
      appointmentDetails && !isEmpty(appointmentDetails) && 'referenceId' in appointmentDetails
        ? appointmentDetails.referenceId
        : '';

    return (
      <Modal
        disableCloseOnEscape={true}
        onClose={onModalClose}
        {...restModalProps}
        css={{
          maxWidth: '600px',
          padding: theme.spacing(3),
          paddingLeft: primaryColor ? theme.spacing(4) : theme.spacing(3),
        }}
      >
        {isLoading ? (
          <AppointmentDetailsModalSpinner />
        ) : (
          <>
            <PersonStatusIndicator personStatus={personStatus} />
            {primaryColor && (
              <div
                css={{
                  backgroundColor: primaryColor ?? PRIMARY_EVENT_COLOR,
                  width: theme.spacing(1),
                  height: '100%',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  borderTopLeftRadius: theme.borderRadius.medium,
                  borderBottomLeftRadius: theme.borderRadius.medium,
                }}
              />
            )}
            <AppointmentEventCard
              appointment={appointmentDetails ?? {}}
              closeAppointmentDetailsModal={onModalClose}
              eventId={appointmentDetails?.id ?? ''}
              referenceId={referenceId as string}
              locationName={getScopeName(locationId) ?? '-'}
            />
          </>
        )}
      </Modal>
    );
  }
);

AppointmentDetailsModal.displayName = 'AppointmentDetailsModal';

const AppointmentDetailsModalSpinner = () => (
  <div css={[styles.flexCenter, { width: '500px', height: '300px' }]}>
    <SpinningLoader />
  </div>
);
